// Doc Order — UI atoms. Ported from the handoff's atoms.jsx / SectionsShared.jsx
// / Insurance.jsx (EncField), retyped for React 18 + TS strict. All visuals are
// driven by the scoped styles.css classes (every rule lives under
// [data-doc-order]). P1 copy rule: provenance tooltips describe the Encompass
// PULL only — no "pushes back on submit" claims (write-back ships in P3).
import { useState, useCallback, useEffect, useRef, type ReactNode, type CSSProperties } from 'react';

// Lucide-style single-path icon set — currentColor strokes (port of icons.js).
export const LW_ICONS: Record<string, string> = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z',
  plus: 'M12 5v14M5 12h14',
  x: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  chevronLeft: 'M15 18l-6-6 6-6',
  arrowLeft: 'M19 12H5M12 19l-7-7 7-7',
  arrowUpRight: 'M7 17L17 7M7 7h10v10',
  building: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-4',
  truck: 'M1 3h15v13H1zM16 8h4l3 3v5h-7M5.5 18.5a2 2 0 1 0 0 .01M18.5 18.5a2 2 0 1 0 0 .01',
  gitBranch: 'M6 3v12M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 6a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 6a9 9 0 0 1-9 9',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2ZM22 6l-10 7L2 6',
  phone: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3a14.55 14.55 0 0 1 4 9 14.55 14.55 0 0 1-4 9 14.55 14.55 0 0 1-4-9 14.55 14.55 0 0 1 4-9Z',
  fileText: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M16 13H8M16 17H8',
  folder: 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2v11Z',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z',
  save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2ZM17 21v-8H7v8M7 3v5h8',
  trash: 'M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
  copy: 'M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2ZM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
  link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  filter: 'M22 3H2l8 9.46V19l4 2v-8.54L22 3Z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z',
  alertCircle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 8v4M12 16h.01',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 3',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  command: 'M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3Z',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42',
  zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8Z',
  moreH: 'M5 12h.01M12 12h.01M19 12h.01',
  externalLink: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3',
  key: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777Zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4',
  refresh: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8M21 3v5h-5M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16M8 16H3v5',
  unlink: 'M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71M5.17 11.75l-1.71 1.71a5 5 0 0 0 7.07 7.07l1.71-1.71M8 2v3M2 8h3M16 19v3M19 16h3',
  mapPin: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0ZM12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  percent: 'M19 5L5 19M6.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM17.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  scale: 'M12 3v18M7 7l-4 8a3 3 0 0 0 8 0L7 7ZM17 7l-4 8a3 3 0 0 0 8 0l-4-8ZM5 7h14',
  penTool: 'M12 19l7-7 3 3-7 7-3-3ZM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5ZM2 2l7.586 7.586M11 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7Z',
  checkCircle: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
  fileCheck: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M9 15l2 2 4-4',
  alertTriangle: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01',
  clipboard: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z',
  layers: 'M12 2L2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
  rotateCcw: 'M1 4v6h6M3.51 15a9 9 0 1 0 2.13-9.36L1 10',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22V15',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  hash: 'M4 9h16M4 15h16M10 3L8 21M16 3l-2 18',
  creditCard: 'M2 5h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM1 10h22',
  lock: 'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2ZM7 11V7a5 5 0 0 1 10 0v4',
  eye: 'M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  edit2: 'M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z',
  refreshCheck: 'M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8M21 3v5h-5M8.5 12.5l2.3 2.3 4.7-4.8'
};

export type IconName = keyof typeof LW_ICONS;

interface IconProps {
  name: IconName | string;
  size?: number;
  className?: string;
  style?: CSSProperties;
  strokeWidth?: number;
}

export function Icon({ name, size = 16, className = '', style, strokeWidth = 1.75 }: IconProps) {
  const path = LW_ICONS[name];
  if (!path) {
return null;
}
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {path.split(' M').map((seg, i) => (
        <path key={i} d={(i === 0 ? '' : 'M') + seg} />
      ))}
    </svg>
  );
}

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'gold';

interface BtnProps {
  children?: ReactNode;
  variant?: BtnVariant;
  size?: string;
  onClick?: () => void;
  icon?: IconName | string;
  iconRight?: IconName | string;
  disabled?: boolean;
  type?: 'button' | 'submit';
  className?: string;
  style?: CSSProperties;
}

export function Btn({
  children,
  variant = 'secondary',
  size,
  onClick,
  icon,
  iconRight,
  disabled,
  type = 'button',
  className = '',
  style
}: BtnProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      // R5 #13: the global `button:disabled:not([data-preserve-disabled])
      // { background: transparent !important }` rule (src/index.css) strips
      // the variant background but not the WHITE label — a disabled primary
      // rendered invisible on the white action bar (the "missing" Send to
      // LO). The attribute is that rule's designed escape hatch; styles.css
      // `.btn:disabled { opacity: .45 }` takes over.
      data-preserve-disabled
      style={style}
      className={`btn btn-${variant} ${size || ''} ${className}`.trim()}
    >
      {icon && <Icon name={icon} size={15} />}
      {children}
      {iconRight && <Icon name={iconRight} size={15} />}
    </button>
  );
}

interface IconBtnProps {
  name: IconName | string;
  onClick?: () => void;
  title?: string;
  size?: number;
  className?: string;
}

export function IconBtn({ name, onClick, title, size = 17, className = '' }: IconBtnProps) {
  return (
    <button className={`icon-btn ${className}`} onClick={onClick} title={title} aria-label={title}>
      <Icon name={name} size={size} />
    </button>
  );
}

type PillKind = 'neutral' | 'info' | 'gold' | 'warn' | 'ok' | 'bad';

interface StatusPillProps {
  kind?: PillKind;
  icon?: IconName | string;
  dot?: boolean;
  children?: ReactNode;
}

export function StatusPill({ kind = 'neutral', icon, dot, children }: StatusPillProps) {
  return (
    <span className={`status-pill ${kind}`}>
      {dot && <span className={`dot ${kind === 'info' ? 'info' : kind}`} />}
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

export function Chip({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <span className={`chip ${className}`}>{children}</span>;
}

// "Pulled from Encompass" flag on read-only auto-filled fields. P1: tooltip
// describes the pull only — write-back copy arrives in P3.
export function EncFlag({ label = 'Encompass' }: { label?: string }) {
  return (
    <span className="chip enc" title="Auto-filled from Encompass">
      <Icon name="link" size={11} />
      {label}
    </span>
  );
}

// Counterpart to EncFlag — marks a section whose contents are internal only.
export function NoSyncFlag({ label = 'Not synced to Encompass' }: { label?: string }) {
  return (
    <span className="chip no-sync" title="Information in this section stays internal to the doc order">
      <Icon name="unlink" size={11} />
      {label}
    </span>
  );
}

interface EncFieldRef {
  id: string | number;
  desc?: string;
}

interface ROFieldProps {
  label: ReactNode;
  value?: ReactNode;
  mono?: boolean;
  span?: boolean | number;
  sub?: ReactNode;
  warn?: boolean;
  warnTip?: string;
  encField?: EncFieldRef;
}

// Read-only field pulled from Encompass (port of SectionsShared.jsx ROField).
export function ROField({ label, value, mono, span, sub, warn, warnTip, encField }: ROFieldProps) {
  const spanClass = span === true ? 'span-2' : span ? `span-${span}` : '';
  const syncTip = encField
    ? `Encompass Field ${encField.id}${encField.desc ? ` — ${encField.desc}` : ''} · synced from Encompass`
    : 'Synced from Encompass';
  return (
    <div className={`field ${spanClass} ${warn ? 'ro-warn' : ''}`}>
      <span className="field-label">
        {label}
        {warn && (
          <span className="ro-warn-tag" title={warnTip || ''}>
            <Icon name="alertTriangle" size={11} />
          </span>
        )}
      </span>
      <span className="field-value-ro" style={mono ? { fontFamily: 'var(--font-mono)', fontSize: 13 } : undefined}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || <span style={{ color: 'var(--fg-subtle)' }}>—</span>}
        </span>
        <span className="enc-dot" title={syncTip}>
          <Icon name="link" size={12} />
        </span>
      </span>
      {sub && <span className={`field-sub ${warn ? 'field-sub-warn' : ''}`}>{sub}</span>}
    </div>
  );
}

interface SumFieldProps {
  label: ReactNode;
  value?: ReactNode;
  mono?: boolean;
  span?: boolean | number;
  sub?: ReactNode;
  warn?: boolean;
  warnTip?: string;
}

// Plain read-only summary field (no Encompass provenance dot).
export function SumField({ label, value, mono, span, sub, warn, warnTip }: SumFieldProps) {
  const spanClass = span === true ? 'span-2' : span ? `span-${span}` : '';
  return (
    <div className={`field ${spanClass} ${warn ? 'ro-warn' : ''}`}>
      <span className="field-label">
        {label}
        {warn && (
          <span className="ro-warn-tag" title={warnTip || ''}>
            <Icon name="alertTriangle" size={11} />
          </span>
        )}
      </span>
      <span className="field-value-ro" style={mono ? { fontFamily: 'var(--font-mono)', fontSize: 13 } : undefined}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || <span style={{ color: 'var(--fg-subtle)' }}>—</span>}
        </span>
      </span>
      {sub && <span className={`field-sub ${warn ? 'field-sub-warn' : ''}`}>{sub}</span>}
    </div>
  );
}

interface EncFieldProps {
  label: ReactNode;
  value?: string | number | null;
  onChange: (value: string | number) => void;
  editable?: boolean;
  type?: string;
  mono?: boolean;
  span?: boolean | number;
  placeholder?: string;
  prefix?: ReactNode;
  warn?: boolean;
  warnNote?: ReactNode;
  format?: 'phone';
  noSync?: boolean;
  encField?: EncFieldRef;
  derived?: boolean;
  derivedNote?: string;
}

// Synced-from-Encompass input (port of Insurance.jsx EncField). Module scope so
// it isn't re-created per parent render (which unmounted the input mid-keystroke
// in the prototype). Phone uses an uncontrolled input to dodge React's
// value-tracker bailout after a non-digit key. P1: no write-back copy.
export function EncField({
  label,
  value,
  onChange,
  editable,
  type = 'text',
  mono,
  span,
  placeholder,
  prefix,
  warn,
  warnNote,
  format,
  noSync,
  encField,
  derived,
  derivedNote
}: EncFieldProps) {
  const spanClass = span === true ? 'span-2' : span ? `span-${span}` : '';
  const isBlank = value === '' || (value === null || value === undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (format === 'phone' && inputRef.current && inputRef.current.value !== (value ?? '')) {
      inputRef.current.value = String(value ?? '');
    }
  });
  const formatPhone = (raw: string) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) {
return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}
    if (digits.length > 3) {
return `${digits.slice(0, 3)}-${digits.slice(3)}`;
}
    return digits;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (format === 'phone') {
      const out = formatPhone(raw);
      if (e.target.value !== out) {
e.target.value = out;
}
      onChange(out);
      return;
    }
    onChange(type === 'number' ? Number(raw || 0) : raw);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (format !== 'phone') {
return;
}
    if (e.metaKey || e.ctrlKey || e.altKey) {
return;
}
    if (e.key.length !== 1) {
return;
}
    if (!/\d/.test(e.key)) {
e.preventDefault();
}
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (format !== 'phone') {
return;
}
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const current = inputRef.current ? inputRef.current.value : '';
    const out = formatPhone(current + pasted);
    if (inputRef.current) {
inputRef.current.value = out;
}
    onChange(out);
  };
  return (
    <div className={`field ${spanClass} ${warn ? 'ro-warn' : ''}`}>
      <span className="field-label">
        {label}
        {warn && (
          <span className="ro-warn-tag">
            <Icon name="alertTriangle" size={11} />
          </span>
        )}
      </span>
      <div className="enc-input-wrap">
        {prefix && <span className="inp-prefix">{prefix}</span>}
        {format === 'phone' ? (
          <input
            ref={inputRef}
            className={`inp ${prefix ? 'inp-with-prefix' : ''} ${warn ? 'inp-warn' : ''} ${isBlank && editable ? 'inp-unset' : ''}`}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            defaultValue={String(value ?? '')}
            disabled={!editable}
            placeholder={placeholder}
            style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={handleChange}
          />
        ) : (
          <input
            className={`inp ${prefix ? 'inp-with-prefix' : ''} ${warn ? 'inp-warn' : ''} ${isBlank && editable ? 'inp-unset' : ''}`}
            type={type}
            value={value ?? ''}
            disabled={!editable}
            placeholder={placeholder}
            style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}
            onChange={handleChange}
          />
        )}
        <span
          className="enc-dot"
          title={
            encField
              ? `Encompass Field ${encField.id}${encField.desc ? ` — ${encField.desc}` : ''}${derived ? ` · ${derivedNote || 'derived'}` : ''} · synced from Encompass`
              : 'Synced from Encompass'
          }
          style={noSync ? { display: 'none' } : undefined}
        >
          <Icon name="link" size={12} />
        </span>
      </div>
      {warn && warnNote && <span className="field-sub field-sub-warn">{warnNote}</span>}
    </div>
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface SegFieldProps<T extends string> {
  options: SegOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  disabled?: boolean;
  unset?: boolean;
  className?: string;
}

// Segmented Yes/No-style control (port of the .seg-field markup used by
// ClosingSection PoA / doc-dating / FeeTable disposition). `unset` paints the
// amber "needs a pick" treatment.
export function SegField<T extends string>({ options, value, onChange, disabled, unset, className = '' }: SegFieldProps<T>) {
  return (
    <div className={`seg-field ${unset ? 'unset' : ''} ${className}`.trim()}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={value === opt.value ? 'active' : ''}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export interface ToastItem {
  id: string;
  message: string;
  icon?: IconName | string;
}

// Lightweight toast stack (port of atoms.jsx useToasts).
export function useToasts(): [(message: string, icon?: IconName | string) => void, ReactNode] {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const add = useCallback((message: string, icon?: IconName | string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, message, icon }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);
  const node = (
    <div className="toast-stack">
      {toasts.map((t) => (
        <div key={t.id} className="toast">
          <Icon name={t.icon || 'check'} size={15} />
          {t.message}
        </div>
      ))}
    </div>
  );
  return [add, node];
}
